import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDescope, useSession } from '@descope/react-sdk';
import { useAlert } from 'context/AlertContext';
import { errorTransformer, ErrorInput } from 'utils/errorTransformer';
import { LoginLayout, ForgotPasswordForm } from 'components/organisms';
import { ForgotPasswordFormData } from 'types/auth.types';

interface DescopeError {
  type?: string;
  message?: string;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const sdk = useDescope();
  const { isSessionLoading } = useSession();
  const { showSuccess, showError, showInfo } = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleSubmit = async (formData: ForgotPasswordFormData): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const redirectUrl = `${window.location.origin}/reset-password`;
      await sdk.password.sendReset(formData.email, redirectUrl);
      
      const successMessage = `Password reset instructions have been sent to ${formData.email}. Please check your inbox and follow the instructions.`;
      setSuccess(successMessage);
      
      showSuccess(successMessage, {
        duration: 10000
      });
      
      showInfo('Didn\'t receive the email? Check your spam folder or try again in a few minutes.', {
        duration: 8000
      });
      
    } catch (err) {
      console.error('Password reset error:', err);
      
      const friendlyMessage = errorTransformer(err as ErrorInput);
      const descopeErr = err as DescopeError;
      
      if (descopeErr.type === 'UserNotFound' || descopeErr.type === 'ActionErrorLoadUserNoUser') {
        const genericMessage = 'If an account with that email exists, we\'ve sent you a password reset link.';
        setSuccess(genericMessage);
        showSuccess(genericMessage);
      } else {
        setError(friendlyMessage);
        showError(friendlyMessage);
      }
      
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  const handleFormChange = (): void => {
    if (error) {
      setError('');
    }
    if (success) {
      setSuccess('');
    }
  };

  return (
    <LoginLayout>
      <ForgotPasswordForm
        onSubmit={handleSubmit}
        onBackToLogin={handleBackToLogin}
        onFormChange={handleFormChange}
        loading={loading || isSessionLoading}
        error={error}
        success={success}
      />
    </LoginLayout>
  );
};

export default ForgotPassword;
